import { Section } from "./Blocks";

/**
 * Dated callout covering Fannie Mae Selling Guide Announcement SEL-2026-08
 * (Sept. 2, 2026), which restructured CONVENTIONAL rental-income requirements.
 * It exists to keep the conventional side of our comparisons current and to
 * state plainly that DSCR qualification is a separate business-purpose
 * framework that this announcement does not govern. No thresholds, overlays or
 * Fannie policy text are reproduced here.
 */

export const FANNIE_SEL_2026_08_URL =
  "https://singlefamily.fanniemae.com/news-events/announcement-sel-2026-08-selling-guide-updates";

export function FannieRentalIncomeUpdateCard({ className = "" }: { className?: string }) {
  return (
    <aside
      className={`rounded-xl border border-brand-gold/50 bg-muted/40 p-5 ${className}`}
      aria-labelledby="fannie-sel-2026-08"
    >
      <div className="text-xs font-semibold uppercase tracking-wide text-brand">
        Conventional guideline update · Sept. 2, 2026
      </div>
      <h3 id="fannie-sel-2026-08" className="mt-1 font-display text-lg font-bold text-foreground">
        Fannie Mae revised conventional rental-income rules (SEL-2026-08)
      </h3>
      <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
        Fannie Mae&apos;s Selling Guide announcement dated Sept. 2, 2026 restructured how
        rental income is treated on <strong>conventional</strong> financing, including revised
        handling of short-term rentals, departing residences, recently acquired investment
        properties, and lease validation and documentation. The rental-income changes are
        mandatory for loan applications dated on or after <strong>Nov. 1, 2026</strong>, and
        lenders may implement them earlier where the announcement allows it.
      </p>
      <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
        This does not change DSCR underwriting. A DSCR loan is business-purpose financing
        qualified primarily on the investment property&apos;s qualifying cash flow or rent under
        the individual lender&apos;s or investor&apos;s program rules — not on Fannie Mae
        guidelines. Documentation and eligibility on a DSCR file vary by lender and program, so
        neither framework is automatically easier or better; the right one depends on the deal
        and the borrower. Comparisons on this page describe general practice, not a specific
        offer, and all terms remain subject to full underwriting.
      </p>
      <p className="mt-3 text-xs text-muted-foreground">
        Source:{" "}
        <a
          href={FANNIE_SEL_2026_08_URL}
          target="_blank"
          rel="noopener noreferrer nofollow"
          className="text-brand underline"
        >
          Fannie Mae Announcement SEL-2026-08
        </a>
        . Verify current conventional requirements with the lender pricing that file.
      </p>
    </aside>
  );
}

export function FannieRentalIncomeUpdateSection({ id = "fannie-sel-2026-08-update" }: { id?: string }) {
  return (
    <Section id={id}>
      <FannieRentalIncomeUpdateCard />
    </Section>
  );
}

/** Shared FAQ pair so visible copy and FAQ schema stay identical wherever used. */
export const FANNIE_SEL_2026_08_FAQS = [
  {
    q: "Does Fannie Mae's SEL-2026-08 rental-income update change DSCR loan qualification?",
    a: "No. SEL-2026-08, dated Sept. 2, 2026, restructured conventional rental-income requirements — including short-term rentals, departing residences, recently acquired investment properties, and lease validation and documentation — with the rental-income changes mandatory for applications dated on or after Nov. 1, 2026. DSCR loans are business-purpose financing qualified primarily on the property's qualifying cash flow or rent under each lender's or investor's own program rules, so those rules apply instead. DSCR documentation and eligibility vary by lender and program.",
  },
  {
    q: "Should I wait until Nov. 1, 2026 to apply for conventional rental financing?",
    a: "That is a question for the lender pricing the conventional file, since some of the changes may be implemented earlier where the announcement permits it. We do not predict how a conventional underwriter will apply them. If you are weighing a conventional loan against a DSCR loan, ask for both structures in writing and compare the actual terms — this is general information, not a commitment to lend, and all terms are subject to full underwriting.",
  },
];
